import { Button } from '@/common/ui/button'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	CardDescription,
} from '@/common/ui/card'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'

import * as z from 'zod'
import useQueryProfile from '@/modules/auth/api/hooks/useQueryProfile'
import Enable2FADialog from '../Enable2FADialog'
import Disable2FADialog from '../Disable2FADialog'
import ResetTOTPDialog from '../ResetTOTPDialog'
import { useState } from 'react'
import useMutationResetPassword from '../../api/hooks/useMutationResetPassword'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import { Skeleton } from '@/common/ui/skeleton'
import { Bell, Key, UserCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { groupNameMapper } from '@/modules/manage-member/mappers'
import { NotificationPermissionButton } from '@/modules/notification/components/NotificationPermissionButton'

const resetPasswordSchema = z.object({
	password: z
		.string({
			invalid_type_error: 'กรุณากรอกรหัสผ่าน',
			required_error: 'กรุณากรอกรหัสผ่าน',
		})
		.min(1, 'กรุณากรอกรหัสผ่าน'),
})

export default function ProfileListing() {
	const [isEnableDialogOpen, setIsEnableDialogOpen] = useState(false)
	const [isDisableDialogOpen, setIsDisableDialogOpen] = useState(false)
	const [isResetDialogOpen, setIsResetDialogOpen] = useState(false)
	const { data: profile, isLoading } = useQueryProfile()
	const { mutate: resetPassword, isPending: isResettingPassword } =
		useMutationResetPassword()

	const resetPasswordForm = useForm<z.infer<typeof resetPasswordSchema>>({
		resolver: zodResolver(resetPasswordSchema),
		defaultValues: {
			password: '',
		},
	})

	const handleResetPassword = (values: z.infer<typeof resetPasswordSchema>) => {
		resetPassword(values, {
			onSuccess: () => {
				toast.success('เปลี่ยนรหัสผ่านสำเร็จ')
				resetPasswordForm.reset()
			},
			onError: (error) => {
				showErrorToast(error)
			},
		})
	}

	if (isLoading) {
		return (
			<div className="space-y-6">
				<Card>
					<CardHeader>
						<Skeleton className="h-8 w-48" />
					</CardHeader>
					<CardContent>
						<div className="space-y-4">
							<Skeleton className="h-6 w-full" />
							<Skeleton className="h-6 w-3/4" />
							<Skeleton className="h-6 w-1/2" />
						</div>
					</CardContent>
				</Card>
			</div>
		)
	}

	if (!profile) {
		return (
			<div className="flex h-full items-center justify-center">
				<p className="text-sm text-muted-foreground">ไม่พบข้อมูล</p>
			</div>
		)
	}

	return (
		<div className="space-y-6">
			<Enable2FADialog
				open={isEnableDialogOpen}
				onOpenChange={setIsEnableDialogOpen}
				id={profile?.id}
			/>
			<Disable2FADialog
				open={isDisableDialogOpen}
				onOpenChange={setIsDisableDialogOpen}
				id={profile?.id}
			/>
			<ResetTOTPDialog
				open={isResetDialogOpen}
				onOpenChange={setIsResetDialogOpen}
				id={profile?.id}
			/>

			{/* Profile Information */}
			<Card>
				<CardHeader>
					<div className="flex items-center space-x-2">
						<UserCircle className="h-6 w-6" />
						<CardTitle>ข้อมูลผู้ใช้</CardTitle>
					</div>
					<CardDescription>
						ข้อมูลส่วนตัวและสิทธิ์การใช้งานของคุณ
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="space-y-4">
						<div className="grid gap-4 md:grid-cols-2">
							<div className="space-y-2">
								<p className="text-sm text-muted-foreground">ชื่อผู้ใช้</p>
								<p className="font-medium">{profile?.username}</p>
							</div>
							<div className="space-y-2">
								<p className="text-sm text-muted-foreground">
									ประเภทผู้ดูแลระบบ
								</p>
								<p className="font-medium">
									{groupNameMapper[profile?.userGroup.name] ||
										profile?.userGroup.name}
								</p>
							</div>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* 2FA Management */}
			<Card>
				<CardHeader>
					<div className="flex items-center space-x-2">
						<Key className="h-6 w-6" />
						<CardTitle>จัดการ Two-Factor Authentication</CardTitle>
					</div>
					<CardDescription>
						เพิ่มความปลอดภัยให้บัญชีของคุณด้วยการยืนยันตัวตนสองชั้น
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="space-y-4">
						<div className="flex items-center space-x-2">
							<div
								className={cn(
									'h-2 w-2 rounded-full',
									profile?.totpEnabled ? 'bg-green-500' : 'bg-red-500',
								)}
							/>
							<p className="text-sm">
								สถานะ: {profile?.totpEnabled ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
							</p>
						</div>
						{!profile?.totpEnabled ? (
							<Button
								onClick={() => setIsEnableDialogOpen(true)}
								className="w-full sm:w-auto"
							>
								เปิดใช้งาน 2FA
							</Button>
						) : (
							<div className="flex flex-col gap-2 sm:flex-row">
								<Button
									variant="destructive"
									onClick={() => setIsDisableDialogOpen(true)}
								>
									ปิดใช้งาน 2FA
								</Button>
								<Button onClick={() => setIsResetDialogOpen(true)}>
									รีเซ็ต TOTP
								</Button>
							</div>
						)}
					</div>
				</CardContent>
			</Card>

			{/* Password Reset */}
			<Card>
				<CardHeader>
					<div className="flex items-center space-x-2">
						<Key className="h-6 w-6" />
						<CardTitle>เปลี่ยนรหัสผ่าน</CardTitle>
					</div>
					<CardDescription>
						ตั้งรหัสผ่านใหม่ที่รัดกุมเพื่อความปลอดภัยของบัญชี
					</CardDescription>
				</CardHeader>
				<CardContent>
					<Form {...resetPasswordForm}>
						<form
							onSubmit={resetPasswordForm.handleSubmit(handleResetPassword)}
							className="space-y-4"
						>
							<FormField
								control={resetPasswordForm.control}
								name="password"
								render={({ field }) => (
									<FormItem>
										<FormLabel>รหัสผ่านใหม่</FormLabel>
										<FormControl>
											<Input
												type="password"
												placeholder="กรอกรหัสผ่านใหม่..."
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<Button type="submit" disabled={isResettingPassword}>
								{isResettingPassword
									? 'กำลังเปลี่ยนรหัสผ่าน...'
									: 'เปลี่ยนรหัสผ่าน'}
							</Button>
						</form>
					</Form>
				</CardContent>
			</Card>

			{/* Notifications */}
			<Card>
				<CardHeader>
					<div className="flex items-center space-x-2">
						<Bell className="h-6 w-6" />
						<CardTitle>การแจ้งเตือน</CardTitle>
					</div>
					<CardDescription>
						เปิดการแจ้งเตือนเพื่อรับข้อความแจ้งเตือนจากระบบ
					</CardDescription>
				</CardHeader>
				<CardContent>
					<NotificationPermissionButton />
				</CardContent>
			</Card>
		</div>
	)
}
